import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import AboutPage from "./pages/AboutPage";
import NewsPage from "./pages/NewsPage";
import CommunityPage from "./pages/CommunityPage";
import CaseStudiesPage from "./pages/CaseStudiesPage";
import TestimonialsPage from "./pages/TestimonialsPage";
import ContactPage from "./pages/ContactPage";
import CareersPage from "./pages/CareersPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AdminDashboard from "./pages/AdminDashboard";
import IntranetPage from "./pages/IntranetPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import { GeoBlockGate } from "./components/GeoBlockGate";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Pages we never want polluting analytics (internal admin tools)
const TRACK_IGNORE_PREFIXES = ['/admin', '/analytics', '/intranet', '/login', '/register'];

// Lightweight page view tracker with duration tracking
function PageTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (TRACK_IGNORE_PREFIXES.some(p => pathname.startsWith(p))) return;

    // Persistent visitor identity (across tabs and sessions)
    let visitorId = localStorage.getItem('nsec_vid');
    const isNewVisitor = !visitorId;
    if (!visitorId) {
      visitorId = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('nsec_vid', visitorId);
    }

    // Per-tab session identity
    let sessionId = sessionStorage.getItem('nsec_sid');
    if (!sessionId) {
      sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('nsec_sid', sessionId);
    }

    // Drop same-origin referrers — they are internal navigation, not a real source
    let referrer = document.referrer || '';
    try {
      if (referrer && new URL(referrer).host === window.location.host) referrer = '';
    } catch (e) { /* malformed referrer header */ }

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams(window.location.search);
    const enterTime = Date.now();

    // Dedupe StrictMode/double-fire by storing a short-lived marker
    const dedupeKey = `nsec_tracked_${pathname}`;
    const lastTracked = Number(sessionStorage.getItem(dedupeKey) || 0);
    if (Date.now() - lastTracked < 1500) return;
    sessionStorage.setItem(dedupeKey, String(Date.now()));

    fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        path: pathname,
        referrer,
        session_id: sessionId,
        visitor_id: visitorId,
        is_new_visitor: isNewVisitor,
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        screen_width: window.innerWidth,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        language: navigator.language || '',
      }),
    }).catch(() => {});
    return () => {
      const duration = Math.round((Date.now() - enterTime) / 1000);
      if (duration > 1) {
        navigator.sendBeacon(`${API_URL}/api/analytics/track-duration`,
          JSON.stringify({ session_id: sessionId, path: pathname, duration }));
      }
    };
  }, [pathname]);
  return null;
}

// Layout wrapper for public pages
function PublicLayout({ children }) {
  return (
    <>
      <SiteHeader />
      {children}
      <SiteFooter />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <GeoBlockGate>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <PageTracker />
            <Routes>
              {/* Public pages with header/footer */}
              <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
              <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
              <Route path="/news" element={<PublicLayout><NewsPage /></PublicLayout>} />
              <Route path="/news/:slug" element={<PublicLayout><NewsPage /></PublicLayout>} />
              <Route path="/community" element={<PublicLayout><CommunityPage /></PublicLayout>} />
              <Route path="/community/:slug" element={<PublicLayout><CommunityPage /></PublicLayout>} />
              <Route path="/case-studies" element={<PublicLayout><CaseStudiesPage /></PublicLayout>} />
              <Route path="/case-studies/:slug" element={<PublicLayout><CaseStudiesPage /></PublicLayout>} />
              <Route path="/testimonials" element={<PublicLayout><TestimonialsPage /></PublicLayout>} />
              <Route path="/careers" element={<PublicLayout><CareersPage /></PublicLayout>} />
              <Route path="/careers/:jobSlug" element={<PublicLayout><CareersPage /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />

              {/* Auth pages (no header/footer) */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              {/* Protected pages (no header/footer - have their own navigation) */}
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/intranet" element={<IntranetPage />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" />
        </AuthProvider>
      </GeoBlockGate>
    </div>
  );
}

export default App;
